/**
 * useBranchConfig — данные и авто-сейв экрана «EO → Конфигуратор → Филиалы».
 *
 * Модель: значения — из глобального useAppData (branches/teaRooms) + слой
 * оптимистичных override'ов на время полёта мутации. Коммит контрола →
 * мгновенный optimistic-апдейт → PUT/POST/DELETE → при успехе flashOk
 * («✓» — единственный фидбэк сохранения, кнопки Save на экране нет) +
 * reload() глобального кеша; при ошибке — откат значения + видимый красный
 * hint (не молча). Оптимистика снимается, когда свежие данные приехали и
 * мутаций в полёте нет.
 *
 * PUT /api/branches/:id — поля по одному, на коммит контрола (perm
 * branches.write — из ролей write она только у EO). Дифф «изменилось ли» —
 * здесь, как в эталоне screen-16-config.js:63,66: без изменения PUT не
 * шлём, но ✓ показываем («значение принято»). Чайные: POST/PUT/DELETE
 * /api/tea-rooms (DELETE — soft, active=0).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { ApiException, type ApiOk, type Branch, type TeaRoom } from "../api/types";
import { useAppData } from "./useAppData";

/** Поля branches, которые правит экран (подмножество whitelist PUT). */
export type BranchPatch = Partial<Pick<Branch,
  "shifting" | "hours_open" | "hours_close" | "tea_cutoff" | "tea_before" |
  "night_cutoff" | "night_end" | "night_surcharge" | "mc_shift" | "uni_pack">>;

export interface TeaDraft {
  name: string;
  price: number;
  capacity: number;
}

/** Фантомная чайная: POST ещё в полёте, id временный — ряд заблокирован. */
export const isPhantomTea = (id: string) => id.startsWith("tmp_");

export interface BranchConfig {
  /** Филиал с наложенной оптимистикой; null — пока не загружен. */
  branch: Branch | null;
  /** Чайные филиала: сервер (sort_order) + оптимистика (правки/добавления/удаления). */
  teas: TeaRoom[];
  /** Скелетон — только пока данные реально грузятся впервые. */
  loading: boolean;
  /** Красный hint сетевой ошибки; null — всё ок. */
  hint: string | null;
  /**
   * flashOk «✓»: ключ ячейки/ряда + счётчик n (React key у потребителей —
   * повторный флэш того же поля ремаунтит ✓/.flash и перезапускает анимации,
   * как эталонный remove class + reflow, CFG:55-56). Гаснет через 900мс.
   */
  flash: { key: string; n: number } | null;
  saveBranch: (patch: BranchPatch, flashKey: string) => void;
  /** id=null — добавление новой чайной. */
  saveTea: (id: string | null, draft: TeaDraft) => void;
  deleteTea: (id: string) => void;
}

export function useBranchConfig(branchId: string): BranchConfig {
  const app = useAppData();
  const [over, setOver] = useState<BranchPatch>({});
  const [teaOver, setTeaOver] = useState<Record<string, TeaDraft>>({});
  const [phantom, setPhantom] = useState<TeaRoom[]>([]);
  const [gone, setGone] = useState<string[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const pendingRef = useRef(0);
  const flashT = useRef(0);
  const branchRef = useRef(branchId);
  branchRef.current = branchId;

  /* Смена филиала — оптимистика, hint и флэш неактуальны. */
  useEffect(() => {
    setOver({}); setTeaOver({}); setPhantom([]); setGone([]); setHint(null);
    setFlash(null); window.clearTimeout(flashT.current);
  }, [branchId]);

  /* Свежие данные приехали и мутаций в полёте нет — оптимистика не нужна. */
  useEffect(() => {
    if (!app.loading && pendingRef.current === 0) {
      setOver({}); setTeaOver({}); setPhantom([]); setGone([]);
    }
  }, [app.loading]);

  useEffect(() => () => window.clearTimeout(flashT.current), []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /* Обёртка мутации: fn возвращает ключ flashOk (или null — без флэша).
     Флэш и hint скоупятся по филиалу: медленный PUT, доехавший после
     переключения, не рисует ложные «✓»/ошибку на чужом филиале. */
  const run = (fn: () => Promise<string | null>, rollback: () => void) => {
    setHint(null);
    const bid = branchRef.current;
    pendingRef.current += 1;
    void fn()
      .then((key) => {
        if (key && branchRef.current === bid) doFlash(key);
        app.reload();
      })
      .catch((err: unknown) => {
        rollback();
        /* reload и при ошибке: честный 404 значит, что цель уже удалена
           другим EO при stale-кеше — без reload несуществующий ряд висел
           бы на экране бессрочно. */
        app.reload();
        if (branchRef.current === bid) {
          setHint(err instanceof ApiException && err.status === 404
            ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
            : "Не сохранилось: ошибка сети. Значение возвращено.");
        }
      })
      .finally(() => { pendingRef.current -= 1; });
  };

  const raw = app.branches.find((x) => x.id === branchId) ?? null;
  const branch = raw ? { ...raw, ...over } : null;
  const teas = app.teaRooms
    .filter((t) => t.branch_id === branchId && !gone.includes(t.id))
    .map((t) => (teaOver[t.id] ? { ...t, ...teaOver[t.id] } : t))
    .sort((a, z) => (a.sort_order ?? 0) - (z.sort_order ?? 0))
    /* Дедуп: после reload серверный ряд уже в teaRooms, а фантом с тем же id
       живёт до очищающего эффекта — без фильтра кадр с дублем ключа. */
    .concat(phantom.filter((t) => !gone.includes(t.id) && !app.teaRooms.some((s) => s.id === t.id)));

  const saveBranch = (patch: BranchPatch, flashKey: string) => {
    if (!branch) return;
    /* Дифф хоста: boolean сравниваем с D1-приведением (1/0 ↔ true/false). */
    const isSame = (old: unknown, next: unknown) =>
      typeof next === "boolean" ? !!old === next : old === next;
    const changed = Object.entries(patch).some(([k, v]) => !isSame(branch[k as keyof Branch], v));
    if (!changed) { doFlash(flashKey); return; }
    const id = branchId;
    setOver((o) => ({ ...o, ...patch }));
    run(
      async () => {
        await api<ApiOk>(`/api/branches/${id}`, { method: "PUT", body: patch });
        return flashKey;
      },
      () => setOver((o) => {
        /* Откат снимает ключ, только если поверх не легло более новое
           значение (у того свой PUT — его исход и решит судьбу ключа). */
        const n = { ...o };
        for (const k of Object.keys(patch) as (keyof BranchPatch)[]) {
          if (n[k] === patch[k]) delete n[k];
        }
        return n;
      }),
    );
  };

  const saveTea = (id: string | null, draft: TeaDraft) => {
    /* Фантом: tmp-id серверу неизвестен — PUT ответил бы честным 404 с
       ложным красным хинтом. UI блокирует ряд до резолва POST, здесь —
       страховка. */
    if (id && isPhantomTea(id)) return;
    if (id) {
      setTeaOver((o) => ({ ...o, [id]: draft }));
      run(
        async () => {
          await api<ApiOk>(`/api/tea-rooms/${id}`, { method: "PUT", body: draft });
          return `tea:${id}`;
        },
        () => setTeaOver((o) => {
          if (o[id] !== draft) return o; // поверх уже более новый драфт со своим PUT
          const n = { ...o };
          delete n[id];
          return n;
        }),
      );
      return;
    }
    /* Добавление: фантом с временным id до reload; после POST id — серверный. */
    const tmp = `tmp_${Date.now()}`;
    const sortOrder = app.teaRooms
      .filter((t) => t.branch_id === branchId)
      .reduce((m, t) => Math.max(m, t.sort_order ?? 0), 0) + 1;
    setPhantom((p) => [...p, {
      id: tmp, branch_id: branchId, name: draft.name, price: draft.price,
      capacity: draft.capacity, sort_order: sortOrder, active: 1,
    }]);
    run(
      async () => {
        const res = await api<ApiOk>("/api/tea-rooms", {
          method: "POST",
          body: { branch_id: branchId, ...draft, sort_order: sortOrder },
        });
        const newId = typeof res.id === "string" ? res.id : tmp;
        setPhantom((p) => p.map((t) => (t.id === tmp ? { ...t, id: newId } : t)));
        return `tea:${newId}`;
      },
      () => setPhantom((p) => p.filter((t) => t.id !== tmp)),
    );
  };

  const deleteTea = (id: string) => {
    /* Фантом: tmp-id серверу неизвестен — DELETE ответил бы честным 404 с
       ложным красным хинтом; свап id после POST к тому же воскресил бы
       ряд. UI блокирует, здесь — страховка. */
    if (isPhantomTea(id)) return;
    setGone((g) => [...g, id]);
    run(
      async () => {
        /* keepalive: DELETE отложен undo-снекбаром (A43.2) и может уходить
           в момент pagehide/сворачивания TMA — обязан пережить выгрузку. */
        await api<ApiOk>(`/api/tea-rooms/${id}`, { method: "DELETE", keepalive: true });
        return null;
      },
      () => setGone((g) => g.filter((x) => x !== id)),
    );
  };

  return {
    branch,
    teas,
    loading: app.loading && app.branches.length === 0,
    hint,
    flash,
    saveBranch,
    saveTea,
    deleteTea,
  };
}
